import type { InputBarTarget } from "@/stores/input-bar-target-store";
import { isSubagentTarget } from "@/lib/input-bar-target-kind";
import { conversationFrameDisplayTitle } from "@/lib/conversation-display";
import { translate } from "@/i18n";
import { inputBarStandaloneMessages } from "@/i18n/messages/input-bar-standalone";
import type { InputTargetRowIconSource } from "./input-target-row-icon";

/** The routed chat's predicted next message, drawn as ghost text. */
interface StandaloneBarPrediction {
  text: string;
  fullRoute?: string;
  /** The routed chat's icon identity, so the placeholder chip matches a picked one. */
  icon: InputTargetRowIconSource;
}

export interface StandaloneBarPlaceholder {
  lead: string;
  route: string;
  tail: string;
  /** Replaces lead/route/tail while the bar is empty, and trails a picked chip
   *  while nothing is typed. */
  prediction: StandaloneBarPrediction | null;
}

/** The four fields InputTargetRowIcon reads, taken from the target instead of a row. */
function iconSource(target: InputBarTarget): InputTargetRowIconSource {
  return {
    id: target.conversationId,
    target_kind: isSubagentTarget(target) ? "subagent" : "chat",
    project_id: target.projectId,
    task: target.taskId ? { id: target.taskId } : null,
  };
}

export function standaloneBarPlaceholder(
  target: InputBarTarget | null,
  startNewConversation: boolean,
  defaultAgentName = "Denker",
  prediction: string | null = null,
): StandaloneBarPlaceholder {
  if (!target || startNewConversation) {
    const tail = translate(inputBarStandaloneMessages, "askAgentAnything", {
      name: defaultAgentName,
    });
    return { lead: "", route: "@new", tail, prediction: null };
  }
  const label = conversationFrameDisplayTitle(
    (target.conversationLabel || target.agentName).trim(),
  );
  const predicted = prediction
    ? {
        text: prediction,
        fullRoute: `@${(target.conversationLabel || target.agentName).trim()}`,
        icon: iconSource(target),
      }
    : null;
  const lead = translate(inputBarStandaloneMessages, "continueLead");
  if (isSubagentTarget(target)) {
    const scope = target.ticket || target.projectName;
    return {
      lead,
      route: `@${label}`,
      tail: scope ? ` · ${scope}` : "",
      prediction: predicted,
    };
  }
  return { lead, route: `@${label}`, tail: "", prediction: predicted };
}
